import CampaignsReportsAPI from '../../api/campaignsReports';
import CampaignsAPI from '../../api/campaigns';

const toUnixTimestamp = (date, endOfDay = false) => {
  if (!date) return null;
  const suffix = endOfDay ? 'T23:59:59' : 'T00:00:00';
  const timestamp = new Date(`${date}${suffix}`).getTime();
  return Number.isNaN(timestamp) ? null : Math.floor(timestamp / 1000);
};

export const state = {
  records: {
    data: [],
    meta: {},
  },
  metrics: {
    summary: {},
    campaigns: [],
    generatedAt: null,
  },
  isLoading: false,
  isLoadingMetrics: false,
  isGenerating: false,
  error: null,
  filters: {
    search: '',
    campaignType: '',
    campaignStatus: '',
    since: '',
    until: '',
  },
};

export const getters = {
  getIsLoading: _state => _state.isLoading,
  getIsLoadingMetrics: _state => _state.isLoadingMetrics,
  getIsGenerating: _state => _state.isGenerating,
  getError: _state => _state.error,
  getFilters: _state => _state.filters,
  getAllCampaignsReports: _state => _state.records?.data || [],
  getCampaignsReportsMeta: _state => _state.records?.meta || {},
  getMetrics: _state => _state.metrics.campaigns,
  getSummary: _state => _state.metrics.summary,
  getGeneratedAt: _state => _state.metrics.generatedAt,
};

export const actions = {
  getCampaignsReportsRequest: async ({ commit }, page = 1) => {
    commit('SET_IS_LOADING', { isLoading: true });
    commit('SET_ERROR', null);
    try {
      const response = await CampaignsReportsAPI.get(page);
      commit('SET_CAMPAIGN_REPORTS', response.data);
    } catch (error) {
      commit('SET_ERROR', error?.message || 'error');
    } finally {
      commit('SET_IS_LOADING', { isLoading: false });
    }
  },

  getMetricsRequest: async ({ commit, state: currentState }) => {
    commit('SET_IS_LOADING_METRICS', { isLoadingMetrics: true });
    commit('SET_ERROR', null);
    try {
      const { search, campaignType, campaignStatus, since, until } =
        currentState.filters;
      const params = {};
      if (search) params.search = search;
      if (campaignType) params.campaign_type = campaignType;
      if (campaignStatus) params.campaign_status = campaignStatus;
      const sinceTimestamp = toUnixTimestamp(since);
      const untilTimestamp = toUnixTimestamp(until, true);
      if (sinceTimestamp) params.since = sinceTimestamp;
      if (untilTimestamp) params.until = untilTimestamp;

      const response = await CampaignsReportsAPI.getMetrics(params);
      commit('SET_METRICS', response.data);
    } catch (error) {
      commit('SET_ERROR', error?.message || 'error');
    } finally {
      commit('SET_IS_LOADING_METRICS', { isLoadingMetrics: false });
    }
  },

  setFilters: ({ commit, dispatch }, filters) => {
    commit('SET_FILTERS', filters);
    return dispatch('getMetricsRequest');
  },

  resetFilters: ({ commit, dispatch }) => {
    commit('RESET_FILTERS');
    return dispatch('getMetricsRequest');
  },

  generateReport: async ({ commit, dispatch }, campaignId) => {
    commit('SET_IS_GENERATING', { isGenerating: true });
    commit('SET_ERROR', null);
    try {
      await CampaignsAPI.report(campaignId);
      return await dispatch('getCampaignsReportsRequest');
    } catch (error) {
      commit('SET_ERROR', error?.message || 'error');
      throw error;
    } finally {
      commit('SET_IS_GENERATING', { isGenerating: false });
    }
  },

  downloadReport: async ({ commit }, { id, title }) => {
    commit('SET_IS_LOADING', { isLoading: true });
    try {
      const response = await CampaignsReportsAPI.reportDownload(id);
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${title}.csv`);
      document.body.appendChild(link);
      link.click();
      // Revoking the object URL synchronously cancels the download in some
      // browsers, so clean up on the next tick instead.
      window.setTimeout(() => {
        link.remove();
        window.URL.revokeObjectURL(url);
      }, 1000);
    } catch (error) {
      commit('SET_ERROR', error?.message || 'error');
      throw error;
    } finally {
      commit('SET_IS_LOADING', { isLoading: false });
    }
  },
};

export const mutations = {
  SET_IS_LOADING(_state, payload) {
    _state.isLoading = payload.isLoading;
  },
  SET_IS_LOADING_METRICS(_state, payload) {
    _state.isLoadingMetrics = payload.isLoadingMetrics;
  },
  SET_IS_GENERATING(_state, payload) {
    _state.isGenerating = payload.isGenerating;
  },
  SET_ERROR(_state, payload) {
    _state.error = payload;
  },
  SET_CAMPAIGN_REPORTS(_state, payload) {
    _state.records = payload;
  },
  SET_METRICS(_state, payload) {
    _state.metrics = {
      summary: payload?.summary || {},
      campaigns: payload?.campaigns || [],
      generatedAt: payload?.generated_at || null,
    };
  },
  SET_FILTERS(_state, payload) {
    _state.filters = { ..._state.filters, ...payload };
  },
  RESET_FILTERS(_state) {
    _state.filters = {
      search: '',
      campaignType: '',
      campaignStatus: '',
      since: '',
      until: '',
    };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
